import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { PageResponse } from '../models/producto.model';
import {
  ProductAttachmentRequest,
  ProductAttachmentResponse,
  ProductAttachmentSearchParams
} from '../models/producto-attachment.model';

@Injectable({
  providedIn: 'root'
})
export class ProductoAttachmentApiService {
  private readonly apiUrl = `${environment.apiUrl}api/products`;

  constructor(private http: HttpClient) { }

  searchProductAttachment(params: ProductAttachmentSearchParams): Observable<PageResponse<ProductAttachmentResponse>> {
    let httpParams = new HttpParams()
      .set('businessId', params.businessId.toString())
      .set('page', (params.page ?? 0).toString())
      .set('size', (params.size ?? 20).toString());
    if (params.attachmentType) httpParams = httpParams.set('attachmentType', params.attachmentType);
    return this.http.get<PageResponse<ProductAttachmentResponse>>(
      `${this.apiUrl}/${params.productId}/attachments`,
      { params: httpParams }
    );
  }

  getProductAttachmentById(productId: number, id: number, businessId: number): Observable<ProductAttachmentResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<ProductAttachmentResponse>(`${this.apiUrl}/${productId}/attachments/${id}`, { params });
  }

  createProductAttachment(productId: number, businessId: number, attachment: ProductAttachmentRequest): Observable<ProductAttachmentResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.post<ProductAttachmentResponse>(`${this.apiUrl}/${productId}/attachments`, attachment, { params });
  }

  updateProductAttachment(productId: number, id: number, businessId: number, attachment: ProductAttachmentRequest): Observable<ProductAttachmentResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.put<ProductAttachmentResponse>(`${this.apiUrl}/${productId}/attachments/${id}`, attachment, { params });
  }

  deleteProductAttachment(productId: number, id: number, businessId: number): Observable<void> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.delete<void>(`${this.apiUrl}/${productId}/attachments/${id}`, { params });
  }

  // ============================================================
  // Incidente #27: documentos REALES (upload/descarga) via módulo storage
  // (POST/GET /api/files). Se guardan como category=PRODUCT_ATTACHMENT,
  // asociados al producto (entityId=productId, entityType=PRODUCT), privados.
  // ============================================================
  private readonly filesUrl = `${environment.apiUrl}api/files`;

  /** Sube un archivo real y lo asocia al producto. */
  uploadDocument(productId: number, file: File): Observable<FileUploadResponse> {
    const form = new FormData();
    form.append('file', file, file.name);
    const params = new HttpParams()
      .set('category', 'PRODUCT_ATTACHMENT')
      .set('entityId', productId.toString())
      .set('entityType', 'PRODUCT')
      .set('isPublic', 'false');
    return this.http.post<FileUploadResponse>(`${this.filesUrl}/upload`, form, { params });
  }

  /** Lista los documentos reales del producto. */
  listDocuments(productId: number): Observable<FileMetadataResponse[]> {
    const params = new HttpParams()
      .set('category', 'PRODUCT_ATTACHMENT')
      .set('entityId', productId.toString())
      .set('entityType', 'PRODUCT');
    return this.http.get<FileMetadataResponse[]>(this.filesUrl, { params });
  }

  /** Descarga el contenido de un documento como blob. */
  downloadDocument(fileId: number): Observable<Blob> {
    return this.http.get(`${this.filesUrl}/${fileId}/download`, { responseType: 'blob' });
  }

  /** Elimina (soft-delete) un documento. */
  deleteDocument(fileId: number): Observable<void> {
    return this.http.delete<void>(`${this.filesUrl}/${fileId}`);
  }
}

export interface FileUploadResponse {
  fileId: number;
  fileName: string;
  originalName: string;
  contentType: string;
  fileSize: number;
  publicUrl?: string;
  isPublic?: boolean;
}

export interface FileMetadataResponse {
  id?: number;
  fileId?: number;
  originalName?: string;
  fileName?: string;
  contentType?: string;
  fileSize?: number;
  createdAt?: string;
  uploadedAt?: string;
}
